<template>
  <page-loader>
    <editor-toolbar
      :is-loading="isLoading"
      :item="offerOriginal.offer.default"
      :item-updated="offerUpdated.offer.default"
      :offer-type="offerOriginal.type"
      :slug="offerOriginal.slug"
      type="offer"
      :publish-chip-editable="true"
      :has-changes="hasChanges"
    />
    <v-divider />
    <v-container>
      <v-alert v-if="!linkedPartners.length" type="info" border="left" prominent>
        You will not be able to publish this offer. It has no linked Partners
      </v-alert>
      <v-tabs v-model="tab" background-color="transparent" class="mb-4">
        <v-tab :disabled="isPerformingAction">Offer Information</v-tab>
        <v-tab :disabled="isPerformingAction">Configuration</v-tab>
        <v-tab :disabled="isPerformingAction">Linked Partners</v-tab>
        <v-tab :disabled="isPerformingAction">Promotions &amp; Ordering</v-tab>
        <v-tab :disabled="isPerformingAction">Shortcodes</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab" class="transparent" continuous>
        <v-tab-item>
          <geo-tabs
            v-model="tabGeo"
            :countries="countriesPredefined"
            :is-disabled="!canPerformActions || !isPermittedTo('update')"
            @add="onAddCountry"
            @delete="onDeleteCountry"
          />
          <v-card class="pa-2" outlined>
            <v-container px-0>
              <v-row v-if="categorisedSchema.general['Imagery']">
                <v-col class="pt-0">
                  <category-options category="Imagery">
                    <v-row>
                      <v-col sm="4">
                        <select-image
                          v-model="offerUpdatedByGeo.logoFeatured"
                          :is-disabled="isDisabled"
                          :title="offerUpdatedDefault.name"
                          :size="250"
                          label="Featured Logo (720x360)"
                          show-extra-previews
                        />
                      </v-col>
                      <v-col sm="4">
                        <select-image
                          v-model="offerUpdatedByGeo.logoThumbnail"
                          :is-disabled="isDisabled"
                          :title="offerUpdatedDefault.name"
                          :size="250"
                          label="Thumbnail Logo (300x300)"
                        />
                      </v-col>
                      <v-col sm="4">
                        <select-image
                          v-model="offerUpdatedByGeo.logoTransparent"
                          :is-disabled="isDisabled"
                          :title="offerUpdatedDefault.name"
                          :size="250"
                          label="Transparent Logo"
                          show-extra-previews
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="pt-0" sm="4">
                        <select-image
                          v-model="offerUpdatedByGeo.logoFunnel"
                          :is-disabled="isDisabled"
                          :title="offerUpdatedDefault.name"
                          :size="250"
                          label="Funnel Logo"
                          show-extra-previews
                        />
                      </v-col>
                    </v-row>
                  </category-options>
                </v-col>
              </v-row>
              <v-row>
                <v-col v-if="categorisedSchema.general['General Information']" sm="8">
                  <category-options
                    category="General Information"
                    :data="offerUpdatedByGeo"
                    :exclude="hiddenProperties"
                    :fields="categorisedSchema.general['General Information']"
                    :is-disabled="isDisabled"
                    :is-loading="isLoading"
                    :is-default="currentCountry === 'default'"
                    @input="onInput"
                  />
                </v-col>
                <v-col v-if="categorisedSchema.general['Features']" sm="4">
                  <category-options
                    category="Features"
                    :data="offerUpdatedByGeo"
                    :exclude="hiddenProperties"
                    :fields="categorisedSchema.general.Features"
                    :is-disabled="isDisabled"
                    :is-loading="isLoading"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col v-for="(fields, category) in categorisedSchemaRemaining" :key="category" sm="6">
                  <category-options
                    :category="category"
                    :data="offerUpdatedByGeo"
                    :exclude="hiddenProperties"
                    :fields="fields"
                    :is-disabled="isDisabled"
                    :is-loading="isLoading"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
              <template v-if="Object.keys(categorisedSchema.language).length">
                <v-row>
                  <v-col>
                    <v-toolbar color="secondary" dark dense flat>
                      <v-toolbar-title class="d-flex align-center">
                        <v-icon class="mr-4">language</v-icon>
                        Language fields
                      </v-toolbar-title>
                      <v-spacer />
                      <v-select
                        v-model="query.params.language"
                        :disabled="isLoading"
                        :items="languageOptions"
                        :loading="isLoading"
                        class="flex-grow-0 context-input"
                        item-text="key"
                        label="Select Language"
                        dense
                        hide-details
                        solo
                      />
                    </v-toolbar>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col v-for="(category, key) in categorisedSchema.language" :key="`language-${category.key}`" sm="6">
                    <category-options
                      :category="key"
                      :context="query.params.language"
                      :data="offerUpdatedByGeo"
                      :exclude="hiddenProperties"
                      :fields="category"
                      :is-disabled="isDisabled"
                      :is-loading="isLoading"
                      :site-groups="siteGroups"
                      @input="onInput"
                    />
                  </v-col>
                </v-row>
              </template>
              <template v-if="Object.keys(categorisedSchema.currency).length">
                <v-row>
                  <v-col>
                    <v-toolbar color="secondary" dark dense flat>
                      <v-toolbar-title class="d-flex align-center">
                        <v-icon class="mr-4">attach_money</v-icon>
                        Currency fields
                      </v-toolbar-title>
                      <v-spacer />
                      <v-select
                        v-model="query.params.currency"
                        :disabled="isLoading"
                        :items="options.mainCurrencies"
                        :loading="isLoading"
                        class="flex-grow-0 context-input"
                        item-text="key"
                        label="Select Currency"
                        dense
                        hide-details
                        solo
                      />
                    </v-toolbar>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col v-for="(category, key) in categorisedSchema.currency" :key="`currency-${category.key}`" sm="6">
                    <category-options
                      :category="key"
                      :context="query.params.currency"
                      :data="offerUpdatedByGeo"
                      :exclude="hiddenProperties"
                      :fields="category"
                      :is-disabled="isDisabled"
                      :is-loading="isLoading"
                      @input="onInput"
                    />
                  </v-col>
                </v-row>
              </template>
            </v-container>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card class="pa-2" outlined>
            <v-row>
              <v-col v-if="categorisedSchema.general.Configuration">
                <category-options
                  category="Configuration"
                  :data="offerUpdatedDefault"
                  :exclude="hiddenProperties"
                  :fields="categorisedSchema.general.Configuration"
                  :is-disabled="isDisabled"
                  :is-loading="isLoading"
                  @input="onInputDefault"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="pt-0">
                <item-list v-model="slugMapUpdated.aliases" :disabled="isDisabled" label="Offer Alias" />
              </v-col>
            </v-row>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-container>
            <v-row>
              <v-col>
                <v-card outlined>
                  <data-table
                    :headers="partnerTableHeaders"
                    :items="mappedLinkedPartners"
                    :route="{ name: 'partners.edit', params: ['slug'] }"
                    disable-sort
                  >
                    <template #item="{ item }">
                      <td>
                        <div class="d-flex align-center">
                          <item-logo :name="item.name" :src="item.logoThumbnail" class="mr-4" />
                          <div>
                            <span>{{ item.name }}</span>
                          </div>
                        </div>
                      </td>
                      <td class="text-left">
                        <published-chip :value="item.published" />
                      </td>
                    </template>
                    <template #actions="{ item: item }">
                      <v-list-item :disabled="!isPermittedTo('delete')" @click="onUnlink(item)">
                        <v-list-item-title>
                          <v-icon left small>link_off</v-icon>
                          <span>Unlink</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                  </data-table>
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-spacer />
              <v-col class="flex-grow-0">
                <v-btn :disabled="isDisabled" color="primary" depressed @click="showLinkModal = true">
                  <v-icon left>add</v-icon>
                  Link Partner
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <offer-order-table :offer="offerOriginal.offer" class="mb-2" />
          <offer-promotions-table :sites="sites" :slug="offerOriginal.slug" :type="offerOriginal.type" class="mb-2" />
        </v-tab-item>
        <v-tab-item>
          <v-card outlined>
            <v-card-text class="py-0">
              <v-row class="my-0">
                <v-col sm="3" class="d-flex align-center">
                  <text-input
                    v-model="shortcodeText"
                    class="flex-grow-1"
                    label="Text"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col sm="3" class="d-flex align-center">
                  <v-btn color="primary" height="40" block depressed @click="copy(shortcode, 'Vist Link Shortcode')">
                    <v-icon left small>file_copy</v-icon>
                    Copy shortcode
                  </v-btn>
                </v-col>
                <v-col sm="6">
                  <div class="overflow-auto py-4">
                    <base-chip color="grey lighten-2" class="max-width-none px-2">
                      <v-icon class="mr-2">link</v-icon>
                      {{ shortcode }}
                    </base-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-container>
    <fab-actions
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      :is-published="offerOriginal.offer.default.published"
      context="Offer"
      @delete="showDeleteModal = true"
      @reset="showResetModal = true"
      @save="onSave"
    />
    <generic-modal
      v-model="showLinkModal"
      :is-disabled="!selectedPartnerToLink && !linkForSelectedPartnerAndOffer"
      :is-loading="isLoading"
      title="Link Partner"
      @modal-confirm="onLinkConfirm"
    >
      <autocomplete-input
        v-model="selectedPartnerToLink"
        :items="mappedPartners"
        label="Select partner to link"
        return-object
      />
      <text-input
        v-model="linkForSelectedPartnerAndOffer"
        :disabled="!selectedPartnerToLink"
        label="Enter a default affiliate link"
      />
    </generic-modal>
    <save-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Changes"
      @modal-confirm="onSaveConfirm"
    >
      <div v-if="saveModalPublishMessage" class="mb-3 subtitle-1 font-weight-bold text-uppercase orange--text">
        {{ saveModalPublishMessage }}
      </div>
      <template v-if="hasChangesOffer">
        <h3 class="text-h5">Offer Information</h3>
        <diff-view :original="offerOriginal.offer" :updated="offerUpdated.offer" :schema="schema" />
      </template>

      <template v-if="hasChangesSlugMap">
        <h3 class="text-h5">Offer aliases</h3>
        <v-list>
          <v-list-item dense>
            <v-list-item-content>
              <v-list-item-subtitle>
                <diff-item
                  v-for="change in hasChangesSlugMap"
                  :key="`${change.value}-diff`"
                  class="d-flex align-center"
                  :change="change"
                />
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </save-modal>
    <generic-modal
      v-model="showUnlinkModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      confirm-color="error"
      title="Unlink Partner from Offer"
      persistent
      @modal-confirm="onUnlinkConfirm"
      @modal-cancel="onUnlinkCancel"
    >
      <div class="pr-12">
        <p>Are you sure that you want to remove {{ pendingUnlinkPartner }}?</p>
        <p v-if="mappedLinkedPartners.length === 1" class="error--text">
          If you remove this partner, there will be no partners assigned to this offer, and it will be set to
          <strong>DRAFT</strong>, and any changes you've made will be <strong>LOST</strong>
        </p>
      </div>
    </generic-modal>
    <delete-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :item="offerOriginal.offer.default.name"
      category="Offer"
      @modal-confirm="onDeleteConfirm"
    />
    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="offer"
      @modal-confirm="onResetConfirm"
    />
    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import { merge } from 'lodash-es'
import Vue from 'vue'
import { mapGetters, mapMutations } from 'vuex'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import BaseChip from '@/components/chips/base-chip'
import DeleteModal from '@/components/modals/delete-modal'
import DiffItem from '@/components/object-diff/diff-item'
import DiffView from '@/components/object-diff/diff-view'
import EditorToolbar from '@/components/editor-toolbar'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import GeoTabs from '@/components/geo-tabs'
import ItemList from '@/components/forms/item-list'
import PageLoader from '@/components/page-loader'
import ResetModal from '@/components/modals/reset-modal'
import SaveModal from '@/components/modals/save-modal'
import SelectImage from '@/components/forms/select-image'
import TextInput from '@/components/forms/text-input'
import DataTable from '@/components/data-table'
import PublishedChip from '@/components/chips/published'
import ItemLogo from '@/components/item-logo'

import CategoryOptions from '@/components/category-options'
import OfferOrderTable from '@/views/offers/components/offer-order-table'
import OfferPromotionsTable from '@/views/offers/components/offer-promotions-table'

// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { languages, links, offers, partners, siteGroups, sites, slugMap } from '@/services/offers'

export default {
  name: 'OfferEditor',
  components: {
    AutocompleteInput,
    BaseChip,
    CategoryOptions,
    DeleteModal,
    DiffItem,
    DiffView,
    EditorToolbar,
    FabActions,
    GenericModal,
    GeoTabs,
    ItemList,
    OfferOrderTable,
    OfferPromotionsTable,
    PageLoader,
    ResetModal,
    SaveModal,
    SelectImage,
    TextInput,
    DataTable,
    PublishedChip,
    ItemLogo,
  },
  mixins: [DataModification, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    isDrafting: false,
    isPublishing: false,
    queryDefaults: ['currency', 'language'],
    queriesDefined: ['currency', 'language'],
    hiddenProperties: ['published', 'partner', 'position', 'paidPosition'],
    partners: [],
    selectedPartnerToLink: null,
    linkForSelectedPartnerAndOffer: null,
    pendingUnlinkPartner: null,
    languageOptions: [],
    linkedPartners: [],
    offerOriginal: { offer: { default: {} } },
    offerUpdated: { offer: { default: {} } },
    queryPagination: false,
    querySearch: false,
    shortcodeText: '',
    shortcodeType: 'inv-visit',
    slugMapOriginal: {
      slug: null,
      full_slug: null,
      aliases: [],
    },
    slugMapUpdated: {
      slug: null,
      full_slug: null,
      aliases: [],
    },
    sites: null,
    siteGroups: null,
    tab: 0,
    tabGeo: 0,
    showDeleteModal: false,
    showLinkModal: false,
    showUnlinkModal: false,
    showResetModal: false,
    showSaveModal: false,
    partnerTableHeaders: [
      { text: 'Partner', value: 'name' },
      { text: 'Status', value: 'published', align: 'left' },
      { value: 'actions' },
    ],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['getItemsForOption', 'options']),
    ...mapGetters('offers', ['categorisedOfferSchema', 'offerTypeSchema']),
    countriesPredefined() {
      return Object.entries(this.offerUpdated.offer)
        .filter(([, values]) => {
          const onlyHasPosition =
            (Object.entries(values).length === 2 &&
              values.position !== undefined &&
              values.paidPosition !== undefined) ||
            (Object.entries(values).length === 1 &&
              (values.position !== undefined || values.paidPosition !== undefined))
          return !onlyHasPosition
        })
        .map(([country]) => country)
        .sort(country => (country === 'default' ? -1 : 0))
    },
    currentCountry() {
      return this.countriesPredefined[this.tabGeo]
    },
    currentCountryFormatted() {
      return this.formatDiffKey('countries', this.currentCountry)
    },
    offerUpdatedDefault() {
      return this.offerUpdated.offer.default
    },
    offerUpdatedByGeo() {
      return this.offerUpdated.offer[this.currentCountry]
    },
    schema() {
      const { type } = this.offerOriginal
      return this.offerTypeSchema(type)
    },
    categorisedSchema() {
      const { type } = this.offerOriginal
      return this.categorisedOfferSchema(type)
    },
    categorisedSchemaRemaining() {
      const ignore = ['Features', 'General Information', 'Imagery', 'Configuration']
      return Object.entries(this.categorisedSchema.general).reduce((acc, [key, data]) => {
        return ignore.includes(key) ? acc : { ...acc, [key]: data }
      }, {})
    },
    hasChangesOffer() {
      return this.hasDataChanged(this.offerOriginal, this.offerUpdated)
    },
    hasChangesSlugMap() {
      const diff = this.getDiff(this.slugMapOriginal, this.slugMapUpdated)
      if (!diff) return false
      return diff.map(change => {
        const type = this.getDiffType(change)
        const value = this.getDiffValue(change, type)
        return { type, value }
      })
    },
    hasChanges() {
      return this.hasChangesOffer || !!this.hasChangesSlugMap
    },
    isPerformingAction() {
      return this.isLoading || this.hasPageErrors
    },
    isDisabled() {
      return this.isPerformingAction || !this.isPermittedTo('update')
    },
    mappedPartners() {
      return this.partners
        .filter(({ slug }) => !this.linkedPartners.find(({ slug: linkedSlug }) => linkedSlug === slug))
        .map(({ name: key, slug: value }) => ({ key, value }))
    },
    mappedLinkedPartners() {
      return this.partners.filter(({ slug }) => this.linkedPartners.find(({ slug: linkedSlug }) => linkedSlug === slug))
    },
    offerType() {
      const offerTypes = this.getItemsForOption('offerTypes')
      return offerTypes.find(({ value }) => value === this.offerOriginal.type) || {}
    },
    canLinkPartners() {
      if (this.offerType.direct) return !this.linkedPartners.length
      return true
    },
    shortcode() {
      const base = `[${this.shortcodeType} offer="${this.offerOriginal.slug}"]`
      const text = this.shortcodeText
      const closingTag = this.shortcodeText ? `[/${this.shortcodeType}]` : null
      return [base, text, closingTag].filter(Boolean).join('')
    },
    saveModalPublishMessage() {
      if (this.offerUpdated.offer.default.published !== this.offerOriginal.offer.default.published) {
        if (this.offerUpdated.offer.default.published) return 'You are publishing this offer'
        return 'You are putting this offer into draft'
      }
      return false
    },
  },
  async created() {
    this.setBeforeUnloadListener(this.onBeforeUnload)
    await Promise.all([this.fetchLanguageOptions(), this.getOfferData(), this.getSlugMapData()])
    this.setPageLoading(false)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    copy(val, text) {
      this.copyToClipboard(val, text)
    },
    copyToClipboard(text, label) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'absolute'
      textArea.style.top = '-100000px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        this.$notify({
          text: `${label} has been copied to your clipboard.`,
        })
      } catch (err) {
        this.$notify({
          type: 'error',
          text: `Unable to copy ${label}`,
        })
      }

      document.body.removeChild(textArea)
    },
    async fetchLanguageOptions() {
      this.isLoading = true
      try {
        this.languageOptions = (await languages.getLanguages()).map(({ name, code }) => ({ key: name, value: code }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translation language options',
          duration: -1,
          data: { error },
        })
      }
      this.isLoading = false
    },
    async getOfferData() {
      try {
        const [offerData, linkedPartners, allPartners, { data: sitesData }, siteGroupsData] = await Promise.all([
          offers.getSingleOffer(this.$route.params.slug),
          offers.getPartnersForOffer(this.$route.params.slug),
          partners.getAllPartners({ compact: true }),
          sites.getAllSites(),
          siteGroups.getAll(),
        ])
        this.offerOriginal = this.clone(offerData)
        this.offerUpdated = this.clone(offerData)
        this.linkedPartners = linkedPartners
        this.partners = allPartners.data
        this.sites = sitesData
        this.siteGroups = siteGroupsData.data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch offer data',
        })
      }
    },
    async getSlugMapData() {
      try {
        this.slugMapOriginal = await slugMap.getMapForSlug(this.$route.params.slug)
        this.slugMapUpdated = this.clone(this.slugMapOriginal)
      } catch (error) {}
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    onInput({ context, model, value }) {
      if (context) {
        if (this.offerUpdatedByGeo[model]) {
          Vue.set(this.offerUpdatedByGeo[model], context, value)
        } else {
          Vue.set(this.offerUpdatedByGeo, model, { [context]: value })
        }
      } else Vue.set(this.offerUpdatedByGeo, model, value)
    },
    onInputDefault({ context, model, value }) {
      if (context) {
        if (this.offerUpdatedDefault[model]) {
          Vue.set(this.offerUpdatedDefault[model], context, value)
        } else {
          Vue.set(this.offerUpdatedDefault, model, { [context]: value })
        }
      } else Vue.set(this.offerUpdatedDefault, model, value)
    },
    onResetConfirm() {
      this.offerUpdated = this.clone(this.offerOriginal)
      this.showResetModal = false
      this.$notify({
        type: 'success',
        text: `Offer "${this.offerUpdatedDefault.name}" reset successfully`,
      })
    },
    async onDeleteConfirm() {
      this.isLoading = true

      try {
        await offers.deleteOffer(this.offerOriginal.slug)
        this.$notify({
          type: 'success',
          text: `Offer "${this.offerUpdatedDefault.name}" deleted successfully`,
        })
        this.$router.push({ name: 'offers.management' })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete offer',
        })
      }

      this.isLoading = false
    },
    onSave() {
      if (!this.linkedPartners.length && this.offerUpdated.offer.default.published) {
        this.isPublishing = false
        return this.$notify({
          type: 'warn',
          text: 'An offer cannot be published if it has no linked partner',
          duration: 10000,
        })
      }
      this.showSaveModal = true
    },
    async onSaveConfirm() {
      this.isLoading = true

      if (this.hasChangesOffer) {
        const { slug, type } = this.offerOriginal
        try {
          await offers.saveSingleOffer(type, slug, this.offerUpdated.offer)
          this.$notify({
            type: 'success',
            text: `Offer "${this.offerUpdatedDefault.name}" saved successfully`,
          })
          await this.getOfferData()
        } catch (error) {
          this.$notify({
            type: 'error',
            text: 'Unable to save offer',
          })
        }
      }

      if (this.hasChangesSlugMap) {
        try {
          if (this.slugMapOriginal.slug === null) {
            await slugMap.createSlugMap(
              merge({}, this.slugMapUpdated, { full_slug: true, slug: this.$route.params.slug })
            )
          } else {
            await slugMap.updateSlugMap(this.slugMapOriginal.slug, this.slugMapUpdated)
          }
          await this.getSlugMapData()
          this.$notify({
            type: 'success',
            text: 'Aliases saved successfully',
          })
        } catch (error) {
          this.$notify({
            type: 'error',
            text: 'Unable to update aliases',
          })
        }
      }

      this.showSaveModal = false
      this.isPublishing = false
      this.isDrafting = false
      this.isLoading = false
    },
    async onLinkConfirm() {
      this.showLinkModal = false
      this.isLoading = true
      if (!this.selectedPartnerToLink || !this.linkForSelectedPartnerAndOffer) return

      try {
        await offers.addPartnerToOffer(this.offerOriginal.slug, this.selectedPartnerToLink.value)
        await links.updateLinksForPartner({
          offer: this.offerOriginal.slug,
          partner: this.selectedPartnerToLink.value,
          links: { default: { default: this.linkForSelectedPartnerAndOffer } },
        })
        this.linkedPartners = await offers.getPartnersForOffer(this.$route.params.slug)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to add partner to offer',
        })
      }

      this.selectedPartnerToLink = null
      this.linkForSelectedPartnerAndOffer = null
      this.isLoading = false
    },
    onUnlink({ slug }) {
      this.pendingUnlinkPartner = slug
      this.showUnlinkModal = true
    },
    async onUnlinkConfirm() {
      this.isLoading = true

      try {
        await offers.removePartnerFromOffer(this.offerOriginal.slug, this.pendingUnlinkPartner)
        this.linkedPartners = await offers.getPartnersForOffer(this.$route.params.slug)
        if (!this.linkedPartners.length) {
          const mergedOffer = merge({}, this.offerOriginal, { offer: { default: { published: false } } })
          const { type, slug, offer } = mergedOffer
          await offers.saveSingleOffer(type, slug, offer)
          const offerData = await offers.getSingleOffer(this.$route.params.slug)
          this.offerOriginal = this.clone(offerData)
          this.offerUpdated = this.clone(offerData)
        }
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to remove partner from offer',
        })
      }

      this.showUnlinkModal = false
      this.pendingUnlinkPartner = null
      this.isLoading = false
    },
    onUnlinkCancel() {
      this.pendingUnlinkPartner = null
    },
    onAddCountry(country) {
      const countryData = merge({}, this.offerUpdated.offer[country], { name: this.offerOriginal.offer.default.name })
      this.tabGeo = 0
      Vue.set(this.offerUpdated.offer, country, countryData)
    },
    onDeleteCountry(country) {
      Vue.delete(this.offerUpdated.offer, country)
    },
  },
}
</script>

<style lang="scss" scoped>
.context-input {
  max-width: 200px;
}
</style>
